import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import DataSource from 'devextreme/data/data_source';
import { Crop, FieldCrop, notify, ThirdpartySystemResourceTypeFields, User } from 'shared';
import { FieldService } from '../stores/field.service';
import { kmlParser, massKmlParser } from 'shared';
import { DxDataGridComponent } from 'devextreme-angular';
import fieldIcon from '@iconify/icons-mdi/shape-polygon-add';
import { LayoutService } from '../home/layout.service';
import { ThirdpartySystemService } from '../stores/thirdparty-system.service';
import { merge } from 'lodash';
import { FieldCropsPopupComponent } from '../field-crops/field-crops-popup/field-crops-popup.component';
import cropIcon from '@iconify/icons-mdi/sprout-outline';
import { CropService } from '../stores/crop.service';

@Component({
  selector: 'app-fields-grid',
  templateUrl: './fields-grid.component.html',
  styleUrls: ['./fields-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class FieldsGridComponent implements OnInit {

  fieldIcon = fieldIcon;

  ds: DataSource;
  canEdit: boolean;
  canAdd: boolean;
  canDelete: boolean;
  canListThirdPartSystems: boolean;
  canEditThirdPartSystems: boolean;

  isLoadingKML: boolean;
  isMassUploadVisible: boolean;
  massUploadDomainID: number;
  massUploadColor = '#f05b41';

  systemFieldsObject = 'thirdparty_systems';
  systemColumns = [];

  @ViewChild(DxDataGridComponent, { static: true })
  grid: DxDataGridComponent;

  @ViewChild(FieldCropsPopupComponent, { static: true })
  fieldCropsPopup: FieldCropsPopupComponent;
  crops: Promise<any>;

  constructor(
    private fieldService: FieldService,
    private cropService: CropService,
    private layoutService: LayoutService,
    private thirdpartySystemService: ThirdpartySystemService,
    public user: User,
  ) {
    // Data source
    this.ds = fieldService.newDataSource();

    // Permissions
    this.canEdit = user.can('edit fields');
    this.canAdd = user.can('add fields');
    this.canDelete = user.can('delete fields');
    this.canListThirdPartSystems = user.can('list thirdparty_systems');
    this.canEditThirdPartSystems = user.can('edit thirdparty_systems');

    // Get custom data
    this.ds.store().on('loaded', (result) => {
      if (!result.hasOwnProperty('custom') || !result.custom) {
        return;
      }
      if (!this.canListThirdPartSystems && !this.canEditThirdPartSystems) {
        return;
      }
      this.systemColumns = result.custom.thirdparty_systems_fields.map((d) => {
        return new ThirdpartySystemResourceTypeFields(d);
      }).reduce((all, current) => {
        all.push(...current.toColumns(this, this.systemFieldsObject));
        return all;
      }, []);

      // this.systemColumns.forEach(c => this.grid.instance.addColumn(c));
    });

    // Fetch crops
    this.crops = cropService.newDataSource({ paginate: false }).load();

    // Bindings
    this.downloadKML = this.downloadKML.bind(this);
    this.codeValidation = this.codeValidation.bind(this);
    this.openEditor = this.openEditor.bind(this);
    this.showCrops = this.showCrops.bind(this);
  }

  ngOnInit(): void {
  }

  hectareUnit(cellInfo) {
    return (parseFloat(cellInfo.value) / 10000).toFixed(2).replace(/\./g, ",");
  }

  onToolbarPreparing(e: any) {
    e.toolbarOptions.items.unshift({
      location: 'after',
      widget: 'dxButton',
      options: {
        hint: 'Charger fichiers KML',
        text: 'KML',
        onClick: this.openMasUpload.bind(this)
      }
    });
    e.toolbarOptions.items.unshift({
      location: 'after',
      widget: 'dxButton',
      options: {
        icon: 'download',
        hint: 'Télecharger fichiers KML',
        onClick: () => {
          this.downloadSelectionKML();
        }
      }
    });
  }

  downloadKML(e: any) {
    this.fieldService.toKML(e.row.data.id).subscribe(data => {

      const filename = e.row.data.name + '.kml';
      const blob = new Blob([data], { type: 'application/vnd.google-earth.kml+xml' });
      // @ts-ignore
      if (window.navigator.msSaveOrOpenBlob) {
        // @ts-ignore
        window.navigator.msSaveBlob(blob, filename);
      } else {
        const elem = window.document.createElement('a');
        elem.href = window.URL.createObjectURL(blob);
        elem.download = filename;
        document.body.appendChild(elem);
        elem.click();
        document.body.removeChild(elem);
      }
    });
  }

  showCrops(e: any) {
    this.fieldCropsPopup.showFieldCrops(e.row.data);
  }

  showCropsFromColumn(field: any) {
    this.fieldCropsPopup.showFieldCrops(field);
  }

  openEditor(e: any) {
    this.layoutService.openTab({
      id: 'field-editor-' + e.row.data.id,
      type: 'component',
      title: 'Editeur Parcelle ' + e.row.data.name,
      componentName: 'field-editor',
      componentState: {
        field: e.row.data,
      }
    });
  }

  private openMasUpload() {
    this.isMassUploadVisible = true;
  }

  handleKMLFiles(files: FileList) {
    if (!files) {
      return;
    }
    this.isLoadingKML = true;
    const promises: Promise<any>[] = [];
    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < files.length; i++) {
      // FileReader instance
      const reader = new FileReader();

      promises.push(new Promise((resolve, reject) => {

        reader.addEventListener('load', () => {
          // const polygons = massKmlParser(reader.result);
          const coords = kmlParser(reader.result);
          resolve({
            name: files[i].name.replace('.kml', ''),
            coords: coords[0].map(c => [c[1], c[0]]),
          });
        }, false);
      }));

      reader.readAsText(files[i]);
    }

    Promise.all(promises).then((values) => {
      this.fieldService.massFileInsert({ domain: this.massUploadDomainID, color: this.massUploadColor, data: values }).subscribe(
        () => {
          notify('Parcelles téléchargés avec succès.', 'success');
          this.grid.instance.refresh();
        },
        () => {
          notify('Les parcelles ne peuvent pas être téléchargés, veuillez réessayer.', 'error');
          this.isLoadingKML = false;
          this.isMassUploadVisible = false;
        },
        () => {
          this.isLoadingKML = false;
          this.isMassUploadVisible = false;
        }
      );
    }
    );
  }

  handleKMLFilesMulti(files: FileList) {
    if (!files) {
      return;
    }
    this.isLoadingKML = true;
    const promises: Promise<any>[] = [];
    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < files.length; i++) {
      // FileReader instance
      const reader = new FileReader();

      promises.push(new Promise((resolve, reject) => {

        reader.addEventListener('load', () => {
          const polygons = massKmlParser(reader.result).map(p => {
            return { name: p.name, coords: p.polygon[0].map(c => [c[1], c[0]]) };
          });
          resolve(polygons);
        }, false);
      }));

      reader.readAsText(files[i]);
    }

    Promise.all(promises).then((values) => {
      this.fieldService.massFileInsert({ domain: this.massUploadDomainID, color: this.massUploadColor, data: values[0] }).subscribe(
        () => {
          notify('Parcelles téléchargés avec succès.', 'success');
          this.grid.instance.refresh();
        },
        () => {
          notify('Les parcelles ne peuvent pas être téléchargés, veuillez réessayer.', 'error');
          this.isLoadingKML = false;
          this.isMassUploadVisible = false;
        },
        () => {
          this.isLoadingKML = false;
          this.isMassUploadVisible = false;
        }
      );
    }
    );
  }

  onRowUpdating(e: any) {
    // if (e.newData.hasOwnProperty(this.systemFieldsObject)) {
    e.newData[this.systemFieldsObject] = merge(e.oldData[this.systemFieldsObject], e.newData[this.systemFieldsObject]);
    // }
  }

  codeValidation(params: any) {
    return this.fieldService.checkCode(params.value, params.data?.id).toPromise();
  }

  checkCode(params: any, system: string, field: string) {
    return this.thirdpartySystemService.checkUnique('FIELD', {
      system,
      field,
      value: params.value,
      id: params.data?.id,
    }).toPromise();
  }

  calculateFilterExpression(filterValue, selectedFilterOperation) {
    const column = this as any;
    return [column.dataField, 'contains', filterValue];
  }

  downloadSelectionKML() {
    const selection = this.grid.instance.getSelectedRowsData().map(d => d.id);
    if (selection.length == 0) {
      return;
    }
    this.isLoadingKML = true;
    this.fieldService.toMassKML(selection).subscribe(
      (data) => {
        const filename = 'selection.kml';
        const blob = new Blob([data], { type: 'application/vnd.google-earth.kml+xml' });
        // @ts-ignore
        if (window.navigator.msSaveOrOpenBlob) {
          // @ts-ignore
          window.navigator.msSaveBlob(blob, filename);
        } else {
          const elem = window.document.createElement('a');
          elem.href = window.URL.createObjectURL(blob);
          elem.download = filename;
          document.body.appendChild(elem);
          elem.click();
          document.body.removeChild(elem);
        }
        this.isLoadingKML = false;
      },
      () => {
        notify('Les parcelles ne peuvent pas être téléchargés, veuillez réessayer.', 'error');
        this.isLoadingKML = false;
      },
      () => {
        this.isLoadingKML = false;
      }
    );
  }
}
